import { Directive, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgentCardDto, AgentCardFilterKey, FrenchDepartment } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { Observable } from 'rxjs';

export type AgentCardColumnKey =
  | 'number'
  | 'companyName'
  | 'siren'
  | 'leader'
  | 'cci'
  | 'departmentCode'
  | 'mentions'
  | 'expiresAt'
  | 'address.departmentCode';

@Directive()
export abstract class AbstractAgentCardsDataTableComponent<T extends AgentCardDto>
  extends BaseDataTableComponent<T, AgentCardColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<AgentCardColumnKey>[]>([
    { key: 'number', label: 'N° de carte', display: true },
    { key: 'companyName', label: 'Raison sociale', display: true },
    { key: 'siren', label: 'SIREN', display: true },
    { key: 'leader', label: 'Représentant', display: true },
    { key: 'cci', label: 'CCI', display: true },
    { key: 'departmentCode', label: 'Département', display: true },
    { key: 'mentions', label: 'Mentions', display: true },
    { key: 'expiresAt', label: 'Expiration', display: true },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (card: AgentCardDto) => this.onItemClicked(card as T),
      disabled: false,
      color: 'primary',
    },
  ]);
  override filters = signal<DataHeaderFilter<AgentCardColumnKey>[]>([
    { column: AgentCardFilterKey.Mentions as AgentCardColumnKey, label: 'Mentions' },
    { column: AgentCardFilterKey.DepartmentCode as AgentCardColumnKey, label: 'Département' },
  ]);

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const mentions = params.getAll(AgentCardFilterKey.Mentions);
      const departmentCode = params.getAll(AgentCardFilterKey.DepartmentCode);

      const filter: Record<string, string[]> = {};
      if (mentions.length > 0) {
        filter[AgentCardFilterKey.Mentions] = mentions;
      }
      if (departmentCode.length > 0) {
        filter[AgentCardFilterKey.DepartmentCode] = departmentCode;
      }

      this.filterChanged({
        changed: true,
        filter,
      });
    });
  }

  onItemClicked(card: T, auxClick?: boolean): void {
    const id = card._id;
    if (!id) return;

    if (auxClick) {
      window.open(`/agent-cards/${id}`, '_blank');
    } else {
      void this.router.navigate(['/agent-cards', id]);
    }
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;

  override getDisplayForEntry(key: AgentCardColumnKey, rawValue: string): string {
    if (key === (AgentCardFilterKey.Mentions as AgentCardColumnKey) || key === 'mentions') {
      const mentionLabels: Record<string, string> = {
        transaction: 'Transaction',
        management: 'Gestion',
        syndic: 'Syndic',
        listBroker: 'Marchand de listes',
        tourism: 'Prestations touristiques',
        service: 'Prestations de services',
      };
      return mentionLabels[rawValue] ?? rawValue;
    }

    if (key === (AgentCardFilterKey.DepartmentCode as AgentCardColumnKey) || key === 'departmentCode') {
      const deptKey = Object.keys(FrenchDepartment).find(
        (k) => FrenchDepartment[k as keyof typeof FrenchDepartment] === rawValue,
      );
      if (deptKey) {
        const formattedName = deptKey
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');
        return `${formattedName} (${rawValue})`;
      }
    }

    return rawValue;
  }
}
